import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const MARKS = [
  { to: "/",                 label: "ROLL 01",  sub: "Inicio",   slug: "inicio" },
  { to: "/galeria/musica",   label: "ROLL 02",  sub: "Música",   slug: "musica" },
  { to: "/galeria/eventos",  label: "ROLL 03",  sub: "Eventos",  slug: "eventos" },
  { to: "/galeria/viajes",   label: "ROLL 04",  sub: "Viajes",   slug: "viajes" },
  { to: "/galeria/retratos", label: "ROLL 05",  sub: "Retratos", slug: "retratos" },
  { to: "/sobre-mi",         label: "ROLL 06",  sub: "Sobre mí", slug: "sobre-mi" },
  { to: "/diario",           label: "ROLL 07",  sub: "Diario",   slug: "diario" },
  { to: "/prints",           label: "ROLL 08",  sub: "Prints",   slug: "prints" },
];

export default function ScrubTimeline() {
  const [progress, setProgress] = useState(0);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(h > 0 ? Math.min(1, Math.max(0, window.scrollY / h)) : 0);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  const activeIdx = MARKS.findIndex((m) =>
    m.to === "/" ? location.pathname === "/" : location.pathname.startsWith(m.to)
  );

  return (
    <>
      {/* LEFT VERTICAL TIMELINE / PLAYHEAD */}
      <aside
        data-testid="scrub-timeline"
        className="fixed left-3 sm:left-5 top-[5vh] bottom-[5vh] z-[55] hidden md:flex flex-col items-center pointer-events-none select-none"
      >
        <div className="relative h-full w-[2px] bg-white/[0.08]">
          <div
            className="absolute left-0 right-0 top-0 bg-white/40 transition-[height] duration-150"
            style={{ height: `${progress * 100}%` }}
          />
          {/* marks */}
          <div className="absolute inset-0 flex flex-col justify-between py-6">
            {MARKS.map((m, i) => (
              <button
                key={m.to}
                onClick={() => navigate(m.to)}
                data-testid={`timeline-mark-${m.slug}`}
                className="pointer-events-auto group relative -translate-x-1/2 left-1/2"
                aria-label={m.sub}
              >
                <span className={`block w-2 h-2 rounded-full border border-white/40 ${
                  i === activeIdx ? "bg-[var(--magenta)] border-[var(--magenta)]" : "bg-black"
                } transition-colors`} />
                <span className="absolute left-4 top-1/2 -translate-y-1/2 whitespace-nowrap font-mono text-[10px] tracking-[0.25em] text-white/40 group-hover:text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  {m.label} · {m.sub}
                </span>
              </button>
            ))}
          </div>
          {/* playhead triangle */}
          <div
            className="absolute -left-[5px] w-0 h-0 transition-[top] duration-200"
            style={{
              top: `${progress * 100}%`,
              borderTop: "5px solid transparent",
              borderBottom: "5px solid transparent",
              borderLeft: "8px solid var(--magenta)",
            }}
          />
        </div>
      </aside>

      {/* TOP-LEFT BRAND */}
      <div className="fixed top-[5vh] left-3 sm:left-12 mt-3 sm:mt-4 z-[65] pointer-events-none">
        <Link
          to="/"
          data-testid="brand-link"
          className="pointer-events-auto inline-flex items-baseline group bg-black/60 backdrop-blur-md border border-white/[0.06] px-3.5 py-2 sm:px-4 sm:py-2.5"
        >
          <span className="font-display uppercase text-xl sm:text-3xl tracking-[0.04em] font-medium text-white">
            EMMANUEL
          </span>
          <span className="font-display uppercase text-xl sm:text-3xl tracking-[0.04em] font-medium text-[var(--magenta)]">
            UPEGUI
          </span>
        </Link>
      </div>

      {/* TOP-RIGHT MENU TRIGGER */}
      <button
        onClick={() => setOpen(true)}
        data-testid="menu-open"
        className="fixed top-[5vh] right-3 sm:right-8 mt-3 sm:mt-4 z-[65] flex items-center gap-3 group bg-black/60 backdrop-blur-md border border-white/[0.06] px-4 py-2.5 hover:border-white/30 transition-colors"
        aria-label="Abrir menú"
      >
        <span className="font-mono text-[10px] sm:text-[11px] tracking-[0.35em] text-white group-hover:text-[var(--magenta)] transition-colors">
          MENU
        </span>
        <span className="flex flex-col gap-1.5">
          <span className="block w-7 h-px bg-white" />
          <span className="block w-7 h-px bg-white" />
        </span>
      </button>

      {/* FULLSCREEN MENU DRAWER */}
      {open && (
        <div
          data-testid="menu-drawer"
          className="fixed inset-0 z-[80] bg-black"
        >
          <div className="absolute top-[5vh] right-3 sm:right-8 mt-3 sm:mt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="menu-close"
              className="font-mono text-[10px] tracking-[0.35em] text-white/70 hover:text-white"
            >
              CLOSE ✕
            </button>
          </div>
          <div className="h-full flex flex-col justify-center px-6 sm:px-16 lg:px-32">
            <p className="font-mono text-[10px] tracking-[0.35em] text-white/40 mb-12">
              CONTACT SHEET · NAVEGACIÓN
            </p>
            <ul className="space-y-1">
              {MARKS.map((m, i) => (
                <li key={m.to} className="border-t border-white/5 first:border-t-0">
                  <Link
                    to={m.to}
                    onClick={() => setOpen(false)}
                    data-testid={`drawer-link-${m.slug}`}
                    className="group flex items-baseline justify-between py-4 sm:py-5 hover:pl-4 transition-all duration-300"
                  >
                    <span className="flex items-baseline gap-6">
                      <span className="font-mono text-[10px] tracking-[0.3em] text-white/30 group-hover:text-[var(--magenta)] transition-colors">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <span className="font-display uppercase tracking-tighter font-medium text-4xl sm:text-6xl lg:text-7xl group-hover:text-[var(--magenta)] transition-colors">
                        {m.sub}
                      </span>
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.3em] text-white/30 group-hover:text-white">
                      {m.label} →
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-16 font-mono text-[10px] tracking-[0.35em] text-white/30 grid grid-cols-2 sm:grid-cols-4 gap-6">
              <a className="hover:text-white" href="https://instagram.com/e.upegui" target="_blank" rel="noreferrer">INSTAGRAM</a>
              <a className="hover:text-white" href="https://www.behance.net/eupegui" target="_blank" rel="noreferrer">BEHANCE</a>
              <a className="hover:text-white" href="https://500px.com/eupegui28" target="_blank" rel="noreferrer">500PX</a>
              <a className="hover:text-white" href="https://wa.me/573017668909" target="_blank" rel="noreferrer">WHATSAPP</a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
